import React, { useState, useEffect, useCallback, PropsWithChildren } from 'react';

export default function Sticky({ children }: PropsWithChildren) {
  const { height, ref } = useMeasureHeight();
  const position = useStickyPosition(height);
  const hideSticky = position > 0;

  return (
    <div
      ref={ref}
      className="top-0 will-change-transform duration-300 transition-transform sticky z-header"
      style={{
        transform: `translateY(-${hideSticky ? 100 : 0}%)`,
      }}
    >
      {children}
    </div>
  );
}

const useStickyPosition = (height: number) => {
  const [pos, setPos] = useState<number>(0);

  useEffect(() => {
    if (!height) return undefined;
    return scrollListener((pageY: number, previous: number) => {
      const isDown = pageY - previous > 0;
      const isNearTop = pageY < height;

      if (isNearTop) {
        setPos(0);
        return;
      }

      if (isDown) {
        setPos(100);
        return;
      }
      setPos(oldPositon =>
        Math.max(0, Math.min(oldPositon + 100 * ((pageY - previous) / height), 100))
      );
    });
  }, [height]);

  return pos;
};

function useMeasureHeight() {
  const [height, setHeight] = useState<number>(0);
  const ref = useCallback((node: HTMLDivElement) => {
    const updateHeight = () => {
      const { offsetHeight } = node;
      setHeight(offsetHeight);
    };
    if (node === null) {
      window.removeEventListener('resize', updateHeight);
    } else {
      updateHeight();
      window.addEventListener('resize', updateHeight);
    }
  }, []);

  return {
    height,
    ref,
  };
}

function scrollListener(
  onScroll: (scrollPosition: number, previousScrollPosition: number) => void
) {
  let previousScrollPosition = window.pageYOffset;
  let currentScrollPosition = previousScrollPosition;
  let scheduledAnimationFrame = false;

  const handleScroll = () => {
    currentScrollPosition = window.pageYOffset;
    if (scheduledAnimationFrame) return;
    scheduledAnimationFrame = true;
    window.requestAnimationFrame(() => {
      if (previousScrollPosition !== currentScrollPosition) {
        onScroll(currentScrollPosition, previousScrollPosition);
        previousScrollPosition = currentScrollPosition;
      }
      scheduledAnimationFrame = false;
    });
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  return () => window.removeEventListener('scroll', handleScroll);
}
